import * as React from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface IconButtonProps extends ButtonProps {
  /**
   * Optional tooltip text. When omitted the button renders without a
   * Radix Tooltip wrapper — callers should pass `aria-label` to keep
   * the icon-only control accessible.
   *
   * [T149-PR8-CONT-8-FE-2] Tooltip became opt-in to fix the
   * "popup-modal" effect on toolbars where every icon auto-revealed
   * a tooltip on touch long-press.
   */
  tooltip?: string;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * When `true` AND `tooltip` is set, the tooltip auto-opens after a
   * 400 ms touch long-press. Defaults to `false` so toolbars and dense
   * control strips don't surface intrusive popovers.
   */
  touchTooltip?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      tooltip,
      tooltipSide = 'top',
      touchTooltip = false,
      onPointerDown,
      onPointerUp,
      onPointerCancel,
      children,
      ...props
    },
    ref,
  ) => {
    const [showMobileTooltip, setShowMobileTooltip] = React.useState(false);
    const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

    const handlePointerDown = React.useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        onPointerDown?.(e);
        if (
          touchTooltip &&
          tooltip &&
          (e.pointerType === 'touch' || e.pointerType === 'pen')
        ) {
          timerRef.current = setTimeout(() => setShowMobileTooltip(true), 400);
        }
      },
      [onPointerDown, touchTooltip, tooltip],
    );

    const handlePointerUp = React.useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        onPointerUp?.(e);
        clearTimeout(timerRef.current);
        if (showMobileTooltip) setTimeout(() => setShowMobileTooltip(false), 1500);
      },
      [onPointerUp, showMobileTooltip],
    );

    const handlePointerCancel = React.useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        onPointerCancel?.(e);
        clearTimeout(timerRef.current);
        setShowMobileTooltip(false);
      },
      [onPointerCancel],
    );

    // ── Tooltip-less mode ────────────────────────────────────────
    // When no `tooltip` prop is provided we render a plain Button.
    // Callers are responsible for supplying `aria-label` so screen
    // readers still get an accessible name for the icon.
    if (!tooltip) {
      return (
        <Button ref={ref} {...props}>
          {children}
        </Button>
      );
    }

    return (
      <TooltipProvider delayDuration={600}>
        <Tooltip open={showMobileTooltip || undefined}>
          <TooltipTrigger asChild>
            <Button
              ref={ref}
              aria-label={props['aria-label'] ?? tooltip}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              {...props}
            >
              {children}
            </Button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
IconButton.displayName = 'IconButton';

export { IconButton };
export type { IconButtonProps };
